import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const HeroSection = () => {
  const { t, isBangla, hasSelectedLanguage } = useLanguage();

  if (!hasSelectedLanguage) return null;

  return (
    <section 
      className="min-h-[80vh] sm:min-h-screen flex flex-col items-center justify-center px-4 py-12 sm:py-16 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #FFF8F0 0%, #F5E6D3 50%, #FFF8F0 100%)'
      }}
      data-testid="hero-section"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 border border-[#D4AF37]/20 rounded-full" />
        <div className="absolute bottom-20 right-10 w-48 h-48 border border-[#800020]/10 rounded-full" />
        <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-[#D4AF37] rounded-full" />
        <div className="absolute bottom-1/3 left-1/4 w-2 h-2 bg-[#D4AF37] rounded-full" />
      </div>

      <div className="relative z-10 text-center max-w-4xl mx-auto">
        {/* Top decorative element */}
        <motion.div 
          className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mb-6 sm:mb-8"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8 }}
        />

        {/* Photos */}
        <motion.div 
          className="flex justify-center items-center gap-4 sm:gap-6 md:gap-10 mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Bride Photo - Anica */}
          <div className="text-center">
            <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full border-4 border-[#D4AF37] overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_b792d1da-6fb1-4b68-8dee-b5d9f4d1c044/artifacts/cg963aqj_WhatsApp%20Image%202026-02-18%20at%2014.08.12%20%281%29.jpeg"
                alt="Anica"
                className="w-full h-full object-cover"
              />
            </div>
            <p className={`mt-3 font-heading text-lg sm:text-xl md:text-2xl text-[#800020] ${isBangla ? 'font-bangla' : ''}`}>
              {isBangla ? 'আনিকা' : 'Anica'}
            </p>
          </div>

          {/* Heart / And symbol */}
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#800020] flex items-center justify-center shadow-lg">
              <span className="text-white text-lg sm:text-xl font-heading">&</span>
            </div>
          </div>

          {/* Groom Photo - Tamjid */}
          <div className="text-center">
            <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full border-4 border-[#D4AF37] overflow-hidden shadow-xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_b792d1da-6fb1-4b68-8dee-b5d9f4d1c044/artifacts/b02dzujv_WhatsApp%20Image%202026-02-18%20at%2014.08.12.jpeg"
                alt="Tamjid"
                className="w-full h-full object-cover"
              />
            </div>
            <p className={`mt-3 font-heading text-lg sm:text-xl md:text-2xl text-[#800020] ${isBangla ? 'font-bangla' : ''}`}>
              {isBangla ? 'তামজিদ' : 'Tamjid'}
            </p>
          </div>
        </motion.div>

        {/* Main Title */}
        <motion.h1 
          className={`font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-[#800020] mb-4 sm:mb-6 px-4 ${isBangla ? 'font-bangla leading-snug' : ''}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          data-testid="hero-title"
        >
          {t('heroTitle')}
        </motion.h1>

        {/* Date */}
        <motion.p 
          className={`text-xl sm:text-2xl md:text-3xl text-[#D4AF37] font-heading ${isBangla ? 'font-bangla' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {t('heroSubtitle')}
        </motion.p>

        {/* Bottom decorative element */}
        <motion.div 
          className="w-16 sm:w-20 mx-auto h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mt-6 sm:mt-8"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        />

        {/* Scroll indicator */}
        <motion.div 
          className="mt-10 sm:mt-14"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className="w-6 h-10 border-2 border-[#D4AF37]/50 rounded-full mx-auto flex justify-center">
            <motion.div 
              className="w-1.5 h-3 bg-[#D4AF37] rounded-full mt-2"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
